// WEEKLY-3 (2026-07-27) — Reuniões Semanais (weekly do gestor).
// Entry do módulo: lista de reuniões + navegação pro detalhe VIA ESTADO
// INTERNO (selecionadaId — sem rota nova de react-router). Deep-link do sino:
// sessionStorage 'op-reunioes' com o id da reunião (consumido no mount).
// Backend: actions op_reuniao_* via POST /comercial/propostas-geradas.

import { useCallback, useEffect, useState } from 'react';
import {
  App as AntApp, Button, Card, DatePicker, Empty, Flex, Input, Modal,
  Select, Space, Spin, Switch, Tag, Typography,
} from 'antd';
import {
  CloudDownloadOutlined, PlusOutlined, SettingOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { Se } from '../components/Se';
import ReuniaoDetalhe from './operacional-reunioes/ReuniaoDetalhe';
import { api, erroMsg, fmtData, fmtSemana, num } from './operacional-reunioes/utils';
import {
  ReuniaoResumo, ReunioesConfig, STATUS_REUNIAO, PERM_GERENCIAR,
} from './operacional-reunioes/types';

const { Text, Title } = Typography;

export default function OperacionalReunioes() {
  return <AntApp><OperacionalReunioesInner /></AntApp>;
}

function OperacionalReunioesInner() {
  const { message } = AntApp.useApp();

  const [reunioes, setReunioes] = useState<ReuniaoResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);

  // Admin: sondado via op_reuniao_config_get (o backend gate é admin — se a
  // chamada passa, o usuário é admin e os botões de config/import aparecem)
  const [ehAdmin, setEhAdmin] = useState(false);
  const [config, setConfig] = useState<ReunioesConfig | null>(null);

  // Modal Nova Weekly
  const [novaAberta, setNovaAberta] = useState(false);
  const [novaData, setNovaData] = useState<Dayjs>(() => dayjs());
  const [novoTitulo, setNovoTitulo] = useState('');
  const [criando, setCriando] = useState(false);

  // Modal Config (admin)
  const [configAberto, setConfigAberto] = useState(false);
  const [cfgAtivo, setCfgAtivo] = useState(true);
  const [cfgDestinatarios, setCfgDestinatarios] = useState<string[]>([]);
  const [salvandoConfig, setSalvandoConfig] = useState(false);

  // Import Notion (admin)
  const [importando, setImportando] = useState(false);
  const [importProgresso, setImportProgresso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('op_reuniao_listar', {});
      setReunioes(Array.isArray(d?.reunioes) ? d.reunioes : []);
    } catch (e: any) {
      message.error('Falha ao carregar reuniões: ' + erroMsg(e));
    } finally { setLoading(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  // Deep-link do sino: sessionStorage 'op-reunioes' → abre a reunião direto
  useEffect(() => {
    const alvo = sessionStorage.getItem('op-reunioes');
    if (alvo) {
      sessionStorage.removeItem('op-reunioes');
      setSelecionadaId(alvo);
    }
  }, []);

  // Sonda admin (config_get só responde pra admin)
  useEffect(() => {
    (async () => {
      try {
        const d = await api('op_reuniao_config_get');
        setEhAdmin(true);
        setConfig({
          ativo: d?.ativo !== false,
          destinatarios_briefing: Array.isArray(d?.destinatarios_briefing)
            ? d.destinatarios_briefing : [],
        });
      } catch { setEhAdmin(false); }
    })();
  }, []);

  async function criarWeekly() {
    setCriando(true);
    try {
      const payload: Record<string, any> = { data_reuniao: novaData.format('YYYY-MM-DD') };
      if (novoTitulo.trim()) payload.titulo = novoTitulo.trim();
      const d = await api('op_reuniao_criar', payload);
      if (d?.ja_existia) message.info('A weekly dessa semana já existe — abrindo a existente.');
      else message.success('Weekly criada.');
      setNovaAberta(false);
      setNovoTitulo('');
      if (d?.id) setSelecionadaId(String(d.id));
      carregar();
    } catch (e: any) {
      message.error('Falha ao criar: ' + erroMsg(e));
    } finally { setCriando(false); }
  }

  function abrirConfig() {
    setCfgAtivo(config?.ativo !== false);
    setCfgDestinatarios(config?.destinatarios_briefing || []);
    setConfigAberto(true);
  }

  async function salvarConfig() {
    const emails = cfgDestinatarios.map(s => s.trim()).filter(Boolean);
    const invalidos = emails.filter(s => !/^\S+@\S+\.\S+$/.test(s));
    if (invalidos.length) {
      message.warning('E-mail inválido: ' + invalidos.join(', '));
      return;
    }
    setSalvandoConfig(true);
    try {
      await api('op_reuniao_config_set', { ativo: cfgAtivo, destinatarios_briefing: emails });
      setConfig({ ativo: cfgAtivo, destinatarios_briefing: emails });
      setConfigAberto(false);
      message.success('Configuração salva.');
    } catch (e: any) {
      message.error('Falha ao salvar: ' + erroMsg(e));
    } finally { setSalvandoConfig(false); }
  }

  // Import do histórico do Notion: chama em loop enquanto tem_mais, com
  // progresso acumulado (padrão batches pra não estourar o timeout da Lambda)
  async function importarNotion() {
    setImportando(true);
    setImportProgresso('Iniciando…');
    let importadas = 0, puladas = 0, rodadas = 0;
    try {
      let temMais = true;
      while (temMais && rodadas < 50) {
        rodadas++;
        const d = await api('op_reuniao_importar_notion', { max_paginas: 10 }, 120000);
        importadas += num(d?.importadas);
        puladas += num(d?.puladas);
        temMais = !!d?.tem_mais;
        setImportProgresso(`${importadas} importada(s), ${puladas} pulada(s)…`);
      }
      message.success(`Import concluído: ${importadas} importada(s), ${puladas} pulada(s).`, 6);
      carregar();
    } catch (e: any) {
      message.error('Falha no import: ' + erroMsg(e));
    } finally {
      setImportando(false);
      setImportProgresso(null);
    }
  }

  // ─── Detalhe aberto → renderiza só ele ─────────────────────────────
  if (selecionadaId) {
    return (
      <div style={{ padding: 16 }}>
        <ReuniaoDetalhe id={selecionadaId}
          onVoltar={() => { setSelecionadaId(null); carregar(); }} />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 6 }}>
        <Title level={4} style={{ margin: 0 }}>🗓️ Reuniões — Weekly</Title>
        <Space wrap>
          {importProgresso && <Tag color="processing">{importProgresso}</Tag>}
          {ehAdmin && (
            <Button icon={<CloudDownloadOutlined />} loading={importando} onClick={importarNotion}>
              Importar histórico do Notion
            </Button>
          )}
          {ehAdmin && (
            <Button icon={<SettingOutlined />} onClick={abrirConfig}
              title="Configuração do briefing automático" />
          )}
          <Se permissao={PERM_GERENCIAR}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => {
              setNovaData(dayjs());
              setNovaAberta(true);
            }}>
              Nova Weekly
            </Button>
          </Se>
        </Space>
      </Flex>
      <Text type="secondary" style={{ display: 'block', marginBottom: 14, fontSize: 13 }}>
        Weekly do gestor: briefing automático, ata, transcrição do Teams e
        encaminhamentos que viram ações nas atividades.
      </Text>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : reunioes.length === 0 ? (
        <Empty description="Nenhuma reunião ainda. Crie a primeira weekly ou importe o histórico do Notion." />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {reunioes.map(r => {
            const st = STATUS_REUNIAO[r.status] || STATUS_REUNIAO.agendada;
            const nEncam = num(r.n_encaminhamentos);
            const nConv = num(r.n_convertidos);
            return (
              <Card key={r.id} size="small" hoverable
                onClick={() => setSelecionadaId(r.id)}
                styles={{ body: { padding: '10px 14px' } }}>
                <Flex align="center" gap={10} wrap="wrap">
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Text strong style={{ color: '#020D7F' }}>{r.titulo}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Semana {fmtSemana(r.semana_ref)}
                        {r.data_reuniao ? ` · reunião ${fmtData(r.data_reuniao)}` : ''}
                      </Text>
                    </div>
                  </div>
                  <Space wrap size={4}>
                    {r.origem === 'notion_import' && (
                      <Tag style={{ fontSize: 11 }}>📥 Notion</Tag>
                    )}
                    <Tag color={st.cor}>{st.label}</Tag>
                    <Tag style={{ fontSize: 11 }}>
                      ✅ {nEncam} encam.
                      {nConv > 0 ? ` · ${nConv} convertido${nConv > 1 ? 's' : ''}` : ''}
                    </Tag>
                  </Space>
                </Flex>
              </Card>
            );
          })}
        </Space>
      )}

      {/* ─── Modal Nova Weekly ─────────────────────────────────── */}
      <Modal open={novaAberta} title="Nova Weekly"
        onCancel={() => setNovaAberta(false)} onOk={criarWeekly}
        confirmLoading={criando} okText="Criar" destroyOnClose>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Text strong style={{ fontSize: 12 }}>Data da reunião</Text>
            <DatePicker value={novaData} allowClear={false} format="DD/MM/YYYY"
              onChange={(d) => { if (d) setNovaData(d); }}
              style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 12 }}>Título (opcional)</Text>
            <Input value={novoTitulo} onChange={e => setNovoTitulo(e.target.value)}
              placeholder={`Weekly — Kick-Off Semanal ${novaData.format('DD/MM')} (default)`}
              style={{ marginTop: 4 }} />
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Só existe 1 weekly por semana — se a semana já tiver uma, ela é aberta.
          </Text>
        </Space>
      </Modal>

      {/* ─── Modal Config (admin) ──────────────────────────────── */}
      <Modal open={configAberto} title="⚙️ Briefing automático da weekly"
        onCancel={() => setConfigAberto(false)} onOk={salvarConfig}
        confirmLoading={salvandoConfig} okText="Salvar" destroyOnClose>
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Flex align="center" gap={10}>
            <Switch checked={cfgAtivo} onChange={setCfgAtivo} />
            <Text style={{ fontSize: 13 }}>
              Gerar e enviar o briefing automaticamente (segunda de manhã)
            </Text>
          </Flex>
          <div>
            <Text strong style={{ fontSize: 12 }}>Destinatários do briefing (e-mails)</Text>
            <Select mode="tags" value={cfgDestinatarios}
              onChange={(v) => setCfgDestinatarios(v as string[])}
              tokenSeparators={[',', ' ', ';']}
              placeholder="Digite um e-mail e Enter"
              style={{ width: '100%', marginTop: 4 }}
              notFoundContent={null} />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              Quem recebe o e-mail com o briefing gerado. Enter ou vírgula adiciona.
            </Text>
          </div>
        </Space>
      </Modal>
    </div>
  );
}
